import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Immutable from 'immutable';

import {
    resultsConfigSelector,
    getAvailableReturnedBrands,
    describedProductsSelector,
    brandCountSelector
} from 'ctm-selectors';
import { get } from 'ctm-util-functions';

import { getFilterStaged } from '../utils';

import { CheckboxListFilter } from '../checkbox_list_filter/checkbox_list_filter';

class _BrandFilter extends Component {

    constructor(props) {
        super(props);

        let options = [];
        if (props.brands) {
            options = props.brands.map((brand) => {
                return {
                    code: get(brand, 'providerMeta.code', brand.code),
                    label: get(brand, 'providerMeta.name', brand.label)
                };
            });
        }
        this.filterProps = { ...props.filterProps, options };
        this.initialiseComponent(props);
    }

    /**
     * Don't render if there are no brands available yet.
     * Initialise with default values
     * @param {Object} props  The incoming set of props
     */
    initialiseComponent = (props) => {
        const { lastViewedPage } = this.props;
        if (props.dontInitFilters || !props.brands.length || props.filterInitialised) {
            return;
        }
        // Retrieve just the brand codes and set them all - the reducer will only set the brands if they have not
        // already been set on state or if the lastViewedPage is not results or more_info
        const filterValues = Immutable.fromJS(props.brands.map((brand) => brand.code || brand.providerMeta.code));
        const filterStaged = getFilterStaged(props.filterProps);

        // Brands should always be checked by default
        props.dispatch({
            type: 'INIT_FILTER_VALUES',
            filter: 'brands',
            filterValues,
            filterStaged,
            force: !['results', 'more_info'].includes(lastViewedPage)
        });
    };

    /**
     * Determine if the component should re-render based on the nextState
     * @param  {Object} nextProps  The incoming set of props
     * @returns {Boolean}           true if the component should re-render
     */
    shouldComponentUpdate = (nextProps) => {
        const { selectedProductIds } = this.props;
        return selectedProductIds.size !== nextProps.selectedProductIds.size;
    };

    /**
     * On each update, remove products from Product Compare basket which brand is filtered
     * @param {Object} nextProps - The incoming set of props
     */
    componentWillUpdate = (nextProps) => {
        const { selectedProductIds, filteredBrands, dispatch } = this.props;
        if (selectedProductIds.size !== 0 && nextProps.filteredBrands !== filteredBrands) {
            const filteredBrandProductsIds = nextProps.describedResults
                .filter((product) => {
                    return !nextProps.filteredBrands.includes(product.getIn(['providerMeta', 'code'])) && nextProps.selectedProductIds.includes(product.getIn(['productMeta', 'id']));
                })
                .map((product) => product.getIn(['productMeta', 'id']));
            filteredBrandProductsIds.valueSeq().forEach((product) => dispatch({
                type: 'TOGGLE_COMPARED_PRODUCT',
                toggle: false,
                filterValue: product
            })
            );
        }
    };

    /**
     * Pass the option props down to the component to render the checkboxes.
     * If you provide options in schema (e.g. health vertical), it will use those options,
     * otherwise it will fetch them from the returned results.
     * @returns {CheckboxListFilter} The returned components
     */
    render = () => {
        const { title, brandCounts } = this.props;
        // Check this, seems redundant
        if (!this.filterProps.options.length) {
            return null;
        }

        return (
            <CheckboxListFilter
                filterProps={this.filterProps}
                title={title}
                featureCounts={brandCounts}
            />
        );
    };

}

_BrandFilter.propTypes = {
    title: PropTypes.string,
    brands: PropTypes.array,
    filteredBrands: PropTypes.instanceOf(Immutable.List),
    // eslint-disable-next-line react/no-unused-prop-types
    describedResults: PropTypes.instanceOf(Immutable.List),
    selectedProductIds: PropTypes.instanceOf(Immutable.List),
    filterProps: PropTypes.shape({
        // append to this, or change to string, if naming new filters
        filter: PropTypes.oneOf(['brands']),
        showUpdateButton: PropTypes.bool,
        fetchNewResults: PropTypes.bool,
        showFeatureCounts: PropTypes.bool,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                label: PropTypes.string
            })
        )
    }).isRequired,
    brandCounts: PropTypes.object.isRequired,
    dispatch: PropTypes.func,
    lastViewedPage: PropTypes.string.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    dontInitFilters: PropTypes.bool
};

/**
 * Map rules to our local component props
 * @param {Object} state - the application state
 * @param {object } props - the component props
 * @returns {Object} - the set of mapped props for the container
 */
const mapStateToProps = (state, props) => {
    const resultsConfig = resultsConfigSelector(state);
    const describedResults = describedProductsSelector(state);
    const filteredBrands = resultsConfig.getIn(['filters', 'brands']) || Immutable.List();
    const selectedProductIds = resultsConfig.getIn(['helpers', 'filters', 'selectedProductIds']) || Immutable.List();
    const brandCounts = props.filterProps.showFeatureCounts ? brandCountSelector(state) : {};
    const filterInitialised = resultsConfig.getIn(['filters', 'initialised', 'brands']);

    return {
        brands: getAvailableReturnedBrands(state, true).toJS() || [],
        lastViewedPage: state.getIn(['page', 'lastViewedPage']),
        describedResults,
        filteredBrands,
        selectedProductIds,
        brandCounts,
        filterInitialised
    };
};

export { _BrandFilter };
export const BrandFilter = connect(mapStateToProps)(_BrandFilter);
